import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import {
    DashboardsServiceV2,
    WidgetDataDTO,
    WidgetDataSetRequestDTO,
    WidgetDefinitionDTO,
    DimensionDefinitionDTO,
    DimensionColumnTypeInd,
} from 'src/app/core/services/dashboardsv2.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { DashboardWidgetLoadingService } from '../../../dashboard-widget-loading-service';

@Component({
    selector: 'app-widget-stats-table',
    templateUrl: './widget-stats-table.component.html',
    styleUrls: ['./widget-stats-table.component.scss'],
})
export class WidgetStatsTableComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() widgetConfig: WidgetDefinitionDTO;
    @Input() dataSetRequest: WidgetDataSetRequestDTO;
    @Input() loadWidgetData: boolean;

    @ViewChild('statsTable') statsTable: ElementRef<HTMLElement>;
    @ViewChildren('autoSize') autoSizeElements!: QueryList<ElementRef<HTMLElement>>;
    @HostListener('window:resize') onResize() {
        this.autoSizeText();
    }

    widgetData: WidgetDataDTO;
    defaultColourSet: string[] = ['#735BA7', '#60C360', '#F47878', '#489292', '#F4B078'];

    subscription: Subscription;
    isLoaded: boolean = false;

    columns: any[] = [];
    headerGroups: any[] = [];
    displayedColumns: string[] = [];
    displayedHeaderGroups: string[] = [];
    dataSource: MatTableDataSource<any[]> = new MatTableDataSource<any[]>();

    private _paginator: MatPaginator;
    @ViewChild(MatPaginator) set paginator(value: MatPaginator) {
        this._paginator = value;
        if (this.dataSource) {
            this.dataSource.paginator = this._paginator;
        }
    }

    private _sort: MatSort;
    @ViewChild(MatSort) set sort(value: MatSort) {
        this._sort = value;
        if (this.dataSource) {
            this.dataSource.sort = this._sort;
        }
    }
    sortActive: string;
    sortDirection: SortDirection; // Corrected type to SortDirection
    tableFilter: string;

    public lastLoadedDataSet: WidgetDataSetRequestDTO | undefined = undefined;

    constructor(
        private dashboardService: DashboardsServiceV2,
        private sanitizer: DomSanitizer,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {}

    ngAfterViewInit(): void {
        const observer = new MutationObserver(() => {
            this.autoSizeText();
        });
        observer.observe(this.statsTable.nativeElement, { childList: true });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasDataSetChangedSinceLastLoaded =
            JSON.stringify(this.dataSetRequest) !== JSON.stringify(this.lastLoadedDataSet);

        if (changes.dataSetRequest && !changes.dataSetRequest.isFirstChange()) {
            if (this.loadWidgetData && this.widgetConfig.version != 3) {
                this.loadData();
            }
        }

        if (this.loadWidgetData && hasDataSetChangedSinceLastLoaded && this.widgetConfig.version != 3) {
            this.loadData();
        }

        if (changes.loadWidgetData && !this.isLoaded) {
            if (changes.loadWidgetData.currentValue && this.widgetConfig.version != 3) {
                this.loadData();
            }
            //stop loading unfinished widget
            if (!changes.loadWidgetData.currentValue && this.subscription) {
                this.subscription.unsubscribe();
                this.widgetConfig.loading = false;
                this._dashboardWidgetLoadingService.remove(this.widgetConfig.id.toString());
            }
        }

        // Handle updates for version 3 dashboards where data is pushed directly via widgetConfig.data
        if (changes.widgetConfig && this.widgetConfig.version === 3) {
            if (this.widgetConfig.data) {
                this.widgetData = this.widgetConfig.data;
                this.initializeTableData(this.widgetData);
            }
        }
    }

    private loadData(): void {
        this.widgetConfig.loading = true;
        this.markAsLoading();

        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }

        if (this.widgetConfig.version === 3 && this.widgetData) {
            // If version 3 and data is already provided, just initialize the table
            this.widgetConfig.loading = false;
            this.markAsLoaded();
            this._updateLastLoadedDataSet();
            this.initializeTableData(this.widgetData);
            return;
        }

        this.subscription = this.dashboardService
            .getDataForWidget(this.widgetConfig, this.dataSetRequest)
            .subscribe((data: WidgetDataDTO) => {
                this.widgetData = data;
                this.widgetConfig.loading = false;
                this.defaultColourSet =
                    this.widgetData.color.length > 0 ? this.widgetData.color : this.defaultColourSet;
                this.markAsLoaded();
                this._updateLastLoadedDataSet();

                if (data) {
                    this.initializeTableData(data);
                }
            });
    }

    private initializeTableData(response: WidgetDataDTO): void {
        this.columns = [];
        this.headerGroups = [];
        this.displayedColumns = [];
        this.displayedHeaderGroups = [];

        response.dimensions
            .filter((dimension: DimensionDefinitionDTO) => !dimension.hidden)
            .map((item, index, array) => {
                const columnDef = index + '-' + item.name.replace(new RegExp(/\s/g), '');
                if (!item.headerColSpan) {
                    this.columns.push({
                        columnDef: columnDef,
                        header: item.name,
                        headerRowSpan: null,
                        displayHeader: item.headerRowSpan == null,
                        class:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar
                                ? 'bar'
                                : this.isNumber(response.data[0]?.[index])
                                ? 'number'
                                : 'text',
                        isBar:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar,
                        headerUrl: item.url,
                        index: index,
                        sticky: index == 0 && !this.isNumber(response.data[0]?.[index]),
                        cellUrl: (row: any) => row[index]?.url ?? null,
                        cell: (row: any) => this.numberWithCommas(row[index]?.displayValue ?? row[index]),
                        cellColour: (row: any) =>
                            item.colours.length > 0
                                ? this.applyHeatMap(row[index], index, item.colours[0], item.colours[1])
                                : 'inherit',
                    });
                    this.displayedColumns.push(columnDef);
                }

                if (item.headerRowSpan) {
                    if (this.columns[this.columns.length - 1]) {
                        this.columns[this.columns.length - 1].class += ' border-right';
                    }
                    this.columns.push({
                        columnDef: columnDef + '-2',
                        header: item.name,
                        headerRowSpan: item.headerRowSpan,
                        displayHeader: true,
                        class:
                            (this.widgetConfig.tableGraph && index == array.length - 1
                                ? 'bar'
                                : this.isNumber(response.data[0]?.[index])
                                ? 'number'
                                : 'text') + ' border-right',
                        isBar:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar,
                        headerUrl: item.url,
                        index: index,
                        sticky: index == 0 && !this.isNumber(response.data[0]?.[index]),
                        cellUrl: (row: any) => row[index]?.url ?? null,
                        cell: (row: any) => this.numberWithCommas(row[index]?.displayValue ?? row[index]),
                        cellColour: (row: any) =>
                            item.colours.length > 0
                                ? this.applyHeatMap(row[index], index, item.colours[0], item.colours[1])
                                : 'inherit',
                    });
                    this.displayedHeaderGroups.push(columnDef + '-2');
                }

                if (item.headerColSpan) {
                    this.headerGroups.push({
                        columnDef: columnDef,
                        name: item.name,
                        colSpan: item.headerColSpan,
                    });
                    if (this.columns[this.columns.length - 1]) {
                        this.columns[this.columns.length - 1].class += ' border-right';
                    }
                    this.displayedHeaderGroups.push(columnDef);
                }
            });

        this.widgetConfig.loading = false;
        this.markAsLoaded();

        if (response.data) {
            //remove hidden dimensions from data
            response.dimensions.forEach((dimension: DimensionDefinitionDTO, index: number) => {
                if (dimension.hidden) {
                    response.data.forEach((row: any[]) => {
                        row.splice(index, 1);
                    });
                }
            });

            this.dataSource = new MatTableDataSource<any[]>(response.data);
            if (this._paginator) {
                this.dataSource.paginator = this._paginator;
            }
            if (this._sort) {
                this.dataSource.sort = this._sort;
            }
            this.dataSource.sortingDataAccessor = (data, sortHeaderId) => this.getSortData(data, sortHeaderId);
            this.dataSource.filterPredicate = (data, filter) => {
                return data.some((value) => {
                    const filterValue: string = this.numberWithCommas(value.displayValue ?? value).toLowerCase();
                    return filterValue.includes(filter);
                });
            };

            // Set default sorting if none is configured
            if (this.widgetConfig.sortFieldIndex == null) {
                // Find the "Customers" column and set it as default sort (descending)
                const customersColumn = this.columns.find((col) => col.header === 'Customers' && col.displayHeader);
                if (customersColumn) {
                    this.widgetConfig.sortFieldIndex = customersColumn.index;
                    this.widgetConfig.sortFieldAscending = false; // false = descending (largest to smallest)
                }
            }

            if (this.widgetConfig.sortFieldIndex != null) {
                this.sortActive = this.columns.find(
                    (col) => col.index == this.widgetConfig.sortFieldIndex && col.displayHeader
                )?.columnDef;
                this.sortDirection = (this.widgetConfig.sortFieldAscending ? 'asc' : 'desc') as SortDirection; // Cast to SortDirection
                if (this._sort) {
                    this._sort.sort({
                        id: this.sortActive,
                        start: this.sortDirection,
                        disableClear: false,
                    });
                }
            }

            if (this.widgetConfig.tableFilter) {
                this.tableFilter = this.widgetConfig.tableFilter;
                this.applyFilter(this.tableFilter);
            } else {
                this.tableFilter = '';
                this.applyFilter('');
            }
        }
    }

    formatString(label: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(label.replace('\n', '<br/>'));
    }

    hasSpace(value: string): boolean {
        if (!value || typeof value !== 'string') return false;
        // Extract plain text content to check for spaces
        const textContent = this.getTextContent(value);
        return textContent.includes(' ');
    }

    getFirstPart(value: string): string {
        if (!value || typeof value !== 'string') return value;
        const textContent = this.getTextContent(value);
        const spaceIndex = textContent.indexOf(' ');
        return spaceIndex > -1 ? textContent.substring(0, spaceIndex) : textContent;
    }

    getSecondPart(value: string): string {
        if (!value || typeof value !== 'string') return '';
        const textContent = this.getTextContent(value);
        const spaceIndex = textContent.indexOf(' ');
        return spaceIndex > -1 ? textContent.substring(spaceIndex + 1) : '';
    }

    private getTextContent(value: string): string {
        if (!value) return '';
        // If the value contains HTML tags, extract text content
        if (/<[^>]*>/.test(value)) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = value;
            return tempDiv.textContent || tempDiv.innerText || '';
        }
        return value;
    }

    columnHasSpacedValues(columnIndex: number): boolean {
        if (!this.widgetData?.data || columnIndex < 0) return false;
        return this.widgetData.data.some((row) => this.hasSpace(row[columnIndex]));
    }

    isHeaderContent(value: string): boolean {
        if (!value || typeof value !== 'string') return false;
        const textContent = this.getTextContent(value);
        const headerValues = ['Who they are?', 'Customers', 'Community', 'Variance', 'Representation'];
        return headerValues.includes(textContent.trim());
    }

    private autoSizeText(): void {
        if (this.autoSizeElements.length === 0) {
            return;
        }

        const elements = this.autoSizeElements.map((element: ElementRef<HTMLElement>) => element.nativeElement);

        elements.forEach((element: HTMLElement) => (element.style.transform = 'scale(1)'));
        const maxWidth = Math.max(...elements.map((element: HTMLElement) => element.offsetWidth));
        const maxHeight = Math.max(...elements.map((element: HTMLElement) => element.offsetHeight));
        elements.forEach((element: HTMLElement) => {
            if (element.children.length > 0 && element.children.item(0).classList.contains('no-scale')) {
                element.style.width = '100%';
            } else {
                const heightScale = element.parentElement.offsetHeight / maxHeight;
                const widthScale = element.parentElement.offsetWidth / maxWidth;

                element.style.transform = `scale(${Math.min(heightScale, widthScale, 5)})`;
            }
        });
    }

    updateWidgetFilter(filterId: string, filterKey: number) {
        const widgetFilter = this.widgetConfig.widgetFilters.find((filter) => filter.filterId === filterId);
        if (widgetFilter.filterSelection != filterKey) {
            widgetFilter.filterSelection = filterKey;
            this.loadData();
        }
    }

    private getSortData(data: any[], sortHeaderId: string): string | number {
        const column = this.columns.find((x) => x.columnDef === sortHeaderId);
        if (column) {
            const value: string = (data[column.index].displayValue ?? data[column.index]).toString();
            //check for and convert value to number
            if (this.isNumber(value)) {
                const numberValue = this.convertStringToNumber(value);
                if (numberValue !== null) {
                    // Check for null from convertStringToNumber
                    return numberValue;
                }
            }
            return value;
        }
        return '';
    }

    private isNumber(value: any): boolean {
        if (typeof value === 'number') {
            return true;
        }
        if (typeof value === 'string') {
            const numberRegex = new RegExp(/^(?:- ?)?(?:[$£] ?)?(?:\d+|\d{1,3}(?:\,\d{3})+)(?:\.\d+)?(?: ?%)?$/);
            return numberRegex.test(value);
        }
        return false;
    }

    private convertStringToNumber(value: any): number {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const strippedValue = value.replace(/[^\d\.-]/g, ''); //strip all but numbers, decimal points, and negative symbols
            if (!isNaN(+strippedValue)) {
                return +strippedValue;
            }
        }
        return null;
    }

    handleFilterKeyUp(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.applyFilter(filterValue);
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
        this.widgetConfig.tableFilter = this.dataSource.filter;

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    numberWithCommas(item: any) {
        if (typeof item === 'number') {
            return item.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else if (typeof item === 'string') {
            return item;
        }
        return item; // Fallback for other types, though typically expecting number or string
    }

    openMapDialog(event: MouseEvent, url: string) {
        event.stopPropagation();
        // this.mapDialogButtonClicked.emit(url); // This line was removed from the new_code, so it's removed here.
    }

    applyHeatMap(value: any, dataIndex: number, lowerColour: string, upperColour: string): string {
        const numberValue = this.convertStringToNumber(value);
        if (numberValue !== null && numberValue !== 0) {
            const dataSet = this.dataSource.data
                .filter((row) => this.isNumber(row[dataIndex]))
                .map((row) => this.convertStringToNumber(row[dataIndex]))
                .filter((val) => val !== null && val !== 0);

            if (dataSet.length > 0) {
                const threshold = dataSet.reduce((a, b) => (numberValue < 0 ? Math.min(a, b) : Math.max(a, b)));
                const percentage = threshold !== 0 ? numberValue / threshold : 0; // Avoid division by zero

                const alphaHex = Math.round(percentage * 255)
                    .toString(16)
                    .padStart(2, '0');

                return (numberValue < 0 ? lowerColour : upperColour) + alphaHex;
            }
        }
        return 'inherit';
    }

    updateSortSelection(event: Sort): void {
        const index = this.columns.find((x) => x.columnDef === event.active)?.index;
        if (index != null) {
            this.widgetConfig.sortFieldIndex = index;
            this.widgetConfig.sortFieldAscending = event.direction === 'asc';
        }
    }

    public markAsLoading(): void {
        this.isLoaded = false;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.add(widgetId);
    }

    public markAsLoaded(): void {
        this.isLoaded = true;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.remove(widgetId);
    }

    private _updateLastLoadedDataSet(): void {
        this.lastLoadedDataSet = { ...this.dataSetRequest };
    }
}

